import { existsSync, mkdirSync, readFileSync, writeFileSync, readdirSync, renameSync, symlinkSync, unlinkSync, readlinkSync, statSync } from 'node:fs';
import type { RouterState } from '../src/modelRouter';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { forComponent, contentText, type Message } from '../src/index';

const log = forComponent('session');

/** Global symlink index: `~/.agent/sessions/<id>.json` → per-project session file. */
const globalDir = () => join(homedir(), '.agent', 'sessions');

/**
 * On-disk session store for the CLI: each conversation is one JSON file at
 * `<cwd>/.agent/sessions/<id>.json`. Writes are atomic (temp + rename) so a crash
 * mid-write never corrupts a prior session. This is what gives the REPL memory across
 * turns and powers `--continue` / `--resume`.
 */
export interface SessionMeta {
  id: string;
  created: number;
  updated: number;
  cwd: string;
  model?: string;
  /** `-m auto` router state (main model + reusable opus side-conversation). */
  router?: RouterState;
  turns: number;
  title: string;
  /** Cumulative tokens across all turns in this session (for `/cost`). */
  tokens?: number;
  /** Cumulative USD cost across all turns (per-turn model pricing; for `/cost`). */
  costUsd?: number;
  /** Sticky: true if any turn's usage was estimated (streamed without provider usage) → cost is approximate. */
  costEstimated?: boolean;
  /** Per-turn forensic log: timing, outcome, usage, and provider error per turn — for investigating a reported issue. */
  events?: TurnEvent[];
}
/** One turn's diagnostics, captured for offline forensics (mirrors what the footer prints, but persisted). */
export interface TurnEvent {
  ts: number;            // turn start (epoch ms)
  durationMs: number;
  model: string;
  finishReason: string;  // 'stop' | 'error' | 'length' | aborted, etc.
  steps: number;
  tools: number;         // tool messages in this turn
  tokens?: number;       // res.usage.totalTokens
  costUsd?: number;
  estimated?: boolean;   // usage was estimated (no provider-reported usage)
  error?: { message: string; statusCode?: number; code?: string };
}
export interface SessionData {
  meta: SessionMeta;
  messages: Message[];
}

export class SessionStore {
  readonly dir: string;
  constructor(cwd: string) {
    this.dir = join(cwd, '.agent', 'sessions');
  }

  /** Sortable, human-readable id: `YYYYMMDD-HHMMSS-<folder>`. */
  newId(now = Date.now(), cwd?: string): string {
    const d = new Date(now);
    const p = (n: number, w = 2) => String(n).padStart(w, '0');
    const slug = (cwd ?? process.cwd()).split('/').pop()?.replace(/[^A-Za-z0-9_-]/g, '') || 'session';
    let id = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}-${slug}`;
    // Disambiguate same-second collisions (concurrent launches / rapid --fork)
    if (existsSync(this.dir) && existsSync(join(this.dir, `${id}.json`))) {
      for (let i = 2; i <= 99; i++) { const c = `${id}-${i}`; if (!existsSync(join(this.dir, `${c}.json`))) { id = c; break; } }
    }
    return id;
  }

  /** A session id must be one safe path segment — blocks `../`-style traversal via --resume/load/save. */
  private safeId(id: string): boolean { return /^[A-Za-z0-9._-]+$/.test(id) && !id.includes('..'); }

  save(data: SessionData): void {
    if (!this.safeId(data.meta.id)) throw new Error(`unsafe session id: ${data.meta.id}`);
    if (!existsSync(this.dir)) mkdirSync(this.dir, { recursive: true });
    const path = join(this.dir, `${data.meta.id}.json`);
    const tmp = `${path}.${process.pid}.tmp`;
    writeFileSync(tmp, JSON.stringify(data));
    renameSync(tmp, path); // atomic swap
    // Global symlink index: ~/.agent/sessions/<id>.json → this file (zero-cost cross-project lookup)
    try {
      const gd = globalDir();
      if (!existsSync(gd)) mkdirSync(gd, { recursive: true });
      const link = join(gd, `${data.meta.id}.json`);
      if (!existsSync(link)) symlinkSync(path, link);
    } catch { /* non-fatal: best-effort index */ }
  }

  load(id: string): SessionData | undefined {
    if (!this.safeId(id)) { log.debug(`rejecting unsafe session id: ${id}`); return undefined; }
    const path = join(this.dir, `${id}.json`);
    if (!existsSync(path)) return undefined;
    try {
      return JSON.parse(readFileSync(path, 'utf8')) as SessionData;
    } catch (e) {
      log.debug(`unreadable session ${id} — ignoring`, e); // corrupt file → caller treats as "no session"
      return undefined;
    }
  }

  /** All sessions' metadata, most-recently-updated first. */
  list(): SessionMeta[] {
    if (!existsSync(this.dir)) return [];
    const metas: SessionMeta[] = [];
    for (const f of readdirSync(this.dir)) {
      if (!f.endsWith('.json')) continue;
      try {
        metas.push((JSON.parse(readFileSync(join(this.dir, f), 'utf8')) as SessionData).meta);
      } catch (e) {
        log.debug(`skipping unreadable session file ${f}`, e);
      }
    }
    return metas.sort((a, b) => b.updated - a.updated);
  }

  /** Cheap listing — ids from filenames + mtime/size from stat, NO parsing (list() parses every file: 870
   *  sessions / 157MB took ~150ms+ synchronously). Most-recently-written first. */
  files(): { id: string; mtime: number; size: number }[] {
    if (!existsSync(this.dir)) return [];
    const out: { id: string; mtime: number; size: number }[] = [];
    for (const f of readdirSync(this.dir)) {
      if (!f.endsWith('.json')) continue;
      try { const st = statSync(join(this.dir, f)); out.push({ id: f.slice(0, -5), mtime: st.mtimeMs, size: st.size }); } catch { /* raced a delete */ }
    }
    return out.sort((a, b) => b.mtime - a.mtime);
  }

  latest(): SessionMeta | undefined {
    return this.list()[0];
  }

  /** The most-recently-updated session's full data (single listing pass). */
  latestData(): SessionData | undefined {
    const m = this.latest();
    return m ? this.load(m.id) : undefined;
  }
}

/** Global session lookup: resolve a (partial) session ID across all projects via the symlink index. */
export function globalSessionLoad(idOrPrefix: string): SessionData | undefined {
  const gd = globalDir();
  if (!existsSync(gd)) return undefined;
  // Exact match first
  const exact = join(gd, `${idOrPrefix}.json`);
  if (existsSync(exact)) {
    try {
      const target = readlinkSync(exact);
      return JSON.parse(readFileSync(target, 'utf8')) as SessionData;
    } catch { return undefined; }
  }
  // Prefix/suffix match (user typed a shortId like "090715-335" = last 10 chars)
  try {
    for (const f of readdirSync(gd)) {
      if (!f.endsWith('.json')) continue;
      const base = f.slice(0, -5);
      if (base.includes(idOrPrefix) || base.endsWith(idOrPrefix)) {
        const target = readlinkSync(join(gd, f));
        return JSON.parse(readFileSync(target, 'utf8')) as SessionData;
      }
    }
  } catch { /* best effort */ }
  return undefined;
}

/** List all sessions globally (from symlink index), most-recently-updated first. */
export function globalSessionList(): SessionMeta[] {
  const gd = globalDir();
  if (!existsSync(gd)) return [];
  const metas: SessionMeta[] = [];
  for (const f of readdirSync(gd)) {
    if (!f.endsWith('.json')) continue;
    try {
      const target = readlinkSync(join(gd, f));
      if (!existsSync(target)) { try { unlinkSync(join(gd, f)); } catch {} continue; } // stale symlink → prune
      metas.push((JSON.parse(readFileSync(target, 'utf8')) as SessionData).meta);
    } catch { /* skip corrupt */ }
  }
  return metas.sort((a, b) => b.updated - a.updated);
}

/** A short, single-line title derived from the first user message. */
export function titleOf(messages: Message[]): string {
  const u = messages.find((m) => m.role === 'user');
  const t = contentText(u?.content).replace(/\s+/g, ' ').trim();
  return t.length > 60 ? t.slice(0, 57) + '…' : t || '(empty)';
}

/** The speakable text of one transcript message, or '' for plumbing (system/tool, synthetic host prompts,
 *  empty tool-call steps). Worker results keep only their <spoken> delivery; host notes/emotion tags drop. */
function recapLine(m: Message): string {
  if (m.role !== 'user' && m.role !== 'assistant') return '';
  let t = contentText(m.content);
  if (m.role === 'user' && /^\[(?:session started|reminder)\]/.test(t)) return '';
  const spoken = [...t.matchAll(/<spoken>([\s\S]*?)<\/spoken>/g)].map((x) => x[1]).join(' ');
  if (spoken) t = spoken;
  t = t.replace(/\s*\[the user interrupted you[\s\S]*$/, '').replace(/\[[a-z_ ]+\]\s*/gi, '').replace(/\s+/g, ' ').trim();
  return t ? `${m.role}: ${t}` : '';
}

/** Cross-session recall for a voice/duplex agent (QuickLook `sessions`): without `which`, the most recent
 *  OTHER sessions of this project with what the user asked; with a session id (exact, or an unambiguous
 *  prefix of the full id — "latest"/"previous" also work), that session's conversation condensed to its
 *  spoken lines. Bounded — it runs synchronously in the voice process and feeds a skinny voice context:
 *  listing is stat-only, and at most 5 files (each ≤ maxParseBytes) are parsed per call. */
export function sessionRecap(store: SessionStore, currentId: string, which?: string, cap = 2500, maxParseBytes = 4_000_000): string {
  const others = store.files().filter((f) => f.id !== currentId);
  if (!others.length) return 'No earlier sessions in this project.';
  const created = (id: string) => { // ids are YYYYMMDD-HHMMSS-<folder>
    const m = id.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime() : NaN;
  };
  const when = (ts: number) => {
    if (!Number.isFinite(ts)) return 'unknown time';
    const d = new Date(ts), today = new Date().toDateString() === d.toDateString();
    return `${today ? 'today' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  };
  const load = (f: { id: string; size: number }) => (f.size <= maxParseBytes ? store.load(f.id) : undefined);
  const w = which?.trim();
  if (!w) {
    const rows = others.slice(0, 5).map((f) => {
      const data = load(f);
      const asks = (data?.messages ?? []).filter((m) => m.role === 'user').map(recapLine).filter(Boolean)
        .slice(0, 3).map((l) => `"${l.slice(6, 86)}"`);
      return `- ${f.id} (${when(created(f.id))}${data ? `, ${data.meta.turns} turns` : ''})${asks.length ? `: ${asks.join('; ')}` : ''}`;
    });
    return `Earlier sessions (newest first) — to read one, call QuickLook with what:"sessions" and path:"<id>":\n${rows.join('\n')}`;
  }
  const recent = others.slice(0, 5).map((f) => f.id).join(', ');
  let hit: { id: string; size: number } | undefined;
  if (/^(latest|last|previous|prev)$/i.test(w)) { // "previous" = the newest one started before this one
    const cur = created(currentId);
    hit = others.filter((f) => created(f.id) < cur).sort((a, b) => created(b.id) - created(a.id))[0] ?? others[0];
  } else {
    hit = others.find((f) => f.id === w);
    if (!hit) {
      const pre = others.filter((f) => f.id.startsWith(w));
      if (pre.length > 1) return `'${w}' matches ${pre.length} sessions — use the full id: ${pre.slice(0, 5).map((f) => f.id).join(', ')}`;
      hit = pre[0];
    }
  }
  if (!hit) return `No session '${w}'. Recent: ${recent}`;
  const data = load(hit);
  if (!data) return `Session ${hit.id} is too large (or unreadable) to recap here — ask a worker (Act) to read it.`;
  let body = data.messages.map(recapLine).filter(Boolean).map((l) => (l.length > 600 ? `${l.slice(0, 600)}…` : l)).join('\n');
  if (body.length > cap) body = `…(earlier lines omitted)\n${body.slice(-cap)}`;
  // The recap is only what was SAID. The reflex filled a missing "why" with an invented motive (live 20260923-192543
  // msg 18: "I assumed you wanted to submit feedback") — so the gap is named where it reads the recap.
  return `Session ${data.meta.id} (${when(data.meta.created)}):\n${body || '(no conversation)'}\n` +
    '(That is everything recorded. Where it does not say why something was done or what was meant, say you do not know — never supply a reason.)';
}
